import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Tabs, Tab, RadioGroup, FormControlLabel, Radio, Checkbox,
  TextField, Button, Rating, LinearProgress, IconButton, SvgIcon, styled,
  CircularProgress,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { localizeCommonMistake } from '../i18n/commonMistakes';

const ThumbsDownIcon = () => (
  <SvgIcon>
    <path d="M15,3H6C5.17,3 4.46,3.5 4.16,4.22L1.14,11.27C1.05,11.5 1,11.74 1,12V14A2,2 0 0,0 3,16H9.31L8.36,20.57C8.34,20.67 8.33,20.77 8.33,20.88C8.33,21.3 8.5,21.67 8.77,21.94L9.83,23L16.41,16.41C16.78,16.05 17,15.55 17,15V5C17,3.89 16.1,3 15,3Z" />
  </SvgIcon>
);

const StyledIconButton = styled(IconButton)(({ theme, active }) => ({
  width: 36,
  height: 36,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: active ? theme.palette.background.paper : theme.palette.error.main,
  '&:hover': {
    backgroundColor: active ? theme.palette.action.hover : theme.palette.error.dark,
  },
  '& .MuiSvgIcon-root': {
    fill: active ? theme.palette.text.secondary : theme.palette.common.white,
    width: 20,
    height: 20,
  },
}));


function ChatEvaluation() {
  const { tr, isEnglish } = useLanguage();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversationData, setConversationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formState, setFormState] = useState({
    a1_correctness: {},
    a2_feedback: {},
    a3_feedback: {},
    b1_correctness: {},
    b2_missed: {},
    b3_expectation: {},
    b3_comment: {},
    b3_tqoe_expectation: {},
    b3_rating: 5,
  });
  const [activeB1Tab, setActiveB1Tab] = useState(0);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Member ID state
  const [memberId, setMemberId] = useState(null);

  // Retrieve member_id from localStorage
  useEffect(() => {
    const getMemberId = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user && user.id) {
            setMemberId(user.id);
            console.log('ChatEvaluation - Member ID:', user.id);
          } else {
             console.log('ChatEvaluation - User data found but no ID');
          }
        } catch (error) {
          console.error('ChatEvaluation - Error parsing user data:', error);
        }
      } else {
        console.log('ChatEvaluation - No user data found in localStorage');
      }
    };
    getMemberId();
  }, []);

  const handleRadioChange = (section, id, value) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [id]: value
      }
    }));
  };

   const handleCheckboxChange = (id, checked) => {
    setFormState(prev => ({
      ...prev,
      b2_missed: {
        ...prev.b2_missed,
        [id]: checked
      }
    }));
  };

  const handleTextChange = (section, id, value) => {
     if (section === 'a2_feedback' || section === 'a3_feedback') {
       setFormState(prev => ({
         ...prev,
         [section]: {
           ...prev[section],
           [id]: { ...prev[section][id], comment: value }
         }
       }));
     } else {
        setFormState(prev => ({
         ...prev,
         b3_comment: {
           ...prev.b3_comment,
           [id]: value
         }
       }));
     }
  };

  const handleToggleFeedback = (section, id) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [id]: { ...prev[section][id], active: !prev[section][id]?.active }
      }
    }));
  };


  const handleB1TabChange = (event, newValue) => {
    setActiveB1Tab(newValue);
  };

  const handleRatingChange = (event, newValue) => {
     setFormState(prev => ({ ...prev, b3_rating: newValue }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!memberId) {
        setSubmitError(tr('無法獲取用戶 ID，請重新登入後再試。', 'Unable to identify the user. Please sign in again.'));
        setIsSubmitting(false);
        return;
    }

    if (!conversationId || !conversationData) {
        setSubmitError(tr('缺少對話 ID 或對話資料，無法提交。', 'The conversation ID or conversation data is missing.'));
        setIsSubmitting(false);
        return;
    }

    // --- Transform data for detailed payload ---
    const payload = {
        memberId: memberId,
        evaluationDetails: {},
        b3_rating: formState.b3_rating // Keep rating separate
    };

    // A1 - Node Correctness Details
    payload.evaluationDetails.a1_correctness_details = [];
    if (conversationData.nodes_correction && conversationData.nodes_correction.length > 0) {
        Object.entries(conversationData.nodes_correction[0]).forEach(([nodeTitle, messages]) => {
            messages.forEach((message, messageIndex) => {
                if (message.role === 'user') {
                    const key = `a1-${nodeTitle}-user-${messageIndex}`;
                    payload.evaluationDetails.a1_correctness_details.push({
                        key: key,
                        nodeTitle: nodeTitle,
                        userMessageContent: message.content,
                        evaluation: formState.a1_correctness[key] || 'correct'
                    });
                }
            });
        });
    }

    // A2 - Response Accuracy Details
    payload.evaluationDetails.a2_feedback_details = [];
    (conversationData.response_correction_rate || []).forEach((item, index) => {
        const key = `a2-resp-${index}`;
        const feedbackState = formState.a2_feedback[key] || { active: false, comment: '' };
        payload.evaluationDetails.a2_feedback_details.push({
            key: key,
            aiResponseContent: item,
            isIncorrect: feedbackState.active === false, // 'active' false means thumbs down (incorrect)
            comment: feedbackState.comment || ''
        });
    });

    // A3 - Guidance Accuracy Details
    payload.evaluationDetails.a3_feedback_details = [];
    (conversationData.advisor_correction_rate || []).forEach((item, index) => {
        const key = `a3-guide-${index}`;
        const feedbackState = formState.a3_feedback[key] || { active: false, comment: '' };
        payload.evaluationDetails.a3_feedback_details.push({
            key: key,
            guidanceContent: item,
            isIncorrect: feedbackState.active === false,
            comment: feedbackState.comment || ''
        });
    });

    // B1 - Checkpoint Accuracy Details
    payload.evaluationDetails.b1_correctness_details = [];
    const nodes = conversationData?.nodesData?.[0]?.nodes || [];
    nodes.forEach(node => {
        (node.checkpoint_status || []).forEach((checkpoint, index) => {
            const key = `b1-cp-${node.id}-${index}`;
            payload.evaluationDetails.b1_correctness_details.push({
                key: key,
                nodeText: node.text,
                checkpointText: checkpoint.text,
                evaluation: formState.b1_correctness[key] || 'correct'
            });
        });
    });

    // B2 - Missed Mistakes Details
    payload.evaluationDetails.b2_missed_details = [];
    (conversationData?.commonMistakes || []).forEach(err => {
        const key = err.id;
        payload.evaluationDetails.b2_missed_details.push({
            key: key,
            commonMistakeText: err.text,
            missed: !!formState.b2_missed[key]
        });
    });

    // B3 - Suggestion Professionalism Details
    payload.evaluationDetails.b3_details = [];
    const overallSuggestion = conversationData?.analysisData?.suggestions?.overall;
    if (overallSuggestion) {
        const key = 'b3-sug-0';
        payload.evaluationDetails.b3_details.push({
            key: key,
            suggestionText: overallSuggestion,
            evaluation: formState.b3_expectation[key] || 'correct',
            comment: formState.b3_comment[key] || ''
        });
    }

    // Add "2個提問1個舉例" evaluation to b3_details
    const tqoeAnalysis = conversationData?.analysisData?.two_questions_one_example_analysis;
    if (tqoeAnalysis) {
        const key = 'b3-tqoe-analysis-0'; // Unique key for this item
        payload.evaluationDetails.b3_details.push({
            key: key,
            suggestionText: tqoeAnalysis.suggestion || '', // Include suggestion text
            evaluation: formState.b3_tqoe_expectation?.[key] || 'correct', // Get user's evaluation
            comment: '' // Currently no comment field for this specific part
        });
    }

    // --- End of transformation ---

    console.log('Submitting Detailed Evaluation Payload:', payload);

    try {
        const response = await axios.post(`${API_URL}/api/conversations/${conversationId}/evaluation`, payload);
        console.log('Submission Response:', response.data);
        setSubmitSuccess(true);
        // Optionally disable form or navigate away
    } catch (err) {
        console.error('Error submitting evaluation:', err);
        const errorMessage = err.response?.data?.error || err.message || tr('提交評估時發生錯誤，請稍後再試。', 'An error occurred while submitting the evaluation. Please try again later.');
        setSubmitError(errorMessage);
    } finally {
        setIsSubmitting(false);
    }
  };

   const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Fetch conversation data
  useEffect(() => {
    const fetchData = async () => {
      if (!conversationId) {
          setError("No conversation ID provided in URL.");
          setIsLoading(false);
          return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/conversations/${conversationId}`);
        console.log("Fetched Conversation Data:", response.data);
        setConversationData(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching conversation data:", err);
        setError(err.message || 'Failed to fetch conversation data.');
        setConversationData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [conversationId]);

  // Initialize default states if not present
  useEffect(() => {
    const initialA1 = {};
    if (conversationData && conversationData.nodes_correction && conversationData.nodes_correction.length > 0) {
        const nodeCorrections = conversationData.nodes_correction[0];
        Object.entries(nodeCorrections).forEach(([nodeTitle, messages]) => {
            messages.forEach((message, messageIndex) => {
                if (message.role === 'user') {
                    const key = `a1-${nodeTitle}-user-${messageIndex}`;
                    initialA1[key] = 'correct';
                }
            });
        });
    }

    const initialA2 = {};
    if (conversationData && conversationData.response_correction_rate) {
        conversationData.response_correction_rate.forEach((item, index) => {
            const key = `a2-resp-${index}`;
            initialA2[key] = { active: false, comment: '' };
        });
    }

     const initialA3 = {};
     if (conversationData && conversationData.advisor_correction_rate) {
         conversationData.advisor_correction_rate.forEach((item, index) => {
             const key = `a3-guide-${index}`;
             initialA3[key] = { active: false, comment: '' };
         });
     }

    const initialB1 = {};
    const nodes = conversationData?.nodesData?.[0]?.nodes || [];
    nodes.forEach(node => {
        (node.checkpoint_status || []).forEach((checkpoint, index) => {
            const key = `b1-cp-${node.id}-${index}`;
            initialB1[key] = 'correct';
        });
    });

    const initialB3Expect = {};
    const initialB3Comment = {};
    const overallSuggestion = conversationData?.analysisData?.suggestions?.overall;
    if (overallSuggestion) {
        const key = 'b3-sug-0';
        initialB3Expect[key] = 'correct';
        initialB3Comment[key] = '';
    }

    // Initialize state for "2個提問1個舉例" evaluation
    const initialB3TQOE = {};
    if (conversationData?.analysisData?.two_questions_one_example_analysis) {
        initialB3TQOE['b3-tqoe-analysis-0'] = 'correct'; // Use the same key as in handleSubmit
    }

    setFormState(prev => ({
      ...prev,
      a1_correctness: { ...initialA1, ...prev.a1_correctness },
      a2_feedback: { ...initialA2, ...prev.a2_feedback },
      a3_feedback: { ...initialA3, ...prev.a3_feedback },
      b1_correctness: { ...initialB1, ...prev.b1_correctness },
      b3_expectation: { ...initialB3Expect, ...prev.b3_expectation },
      b3_comment: { ...initialB3Comment, ...prev.b3_comment },
      b3_tqoe_expectation: { ...initialB3TQOE, ...prev.b3_tqoe_expectation },
    }));
  }, [conversationData]);

  // Navigate back to scenarios after successful submission
  useEffect(() => {
    let timerId;
    if (submitSuccess) {
      console.log("Evaluation successful, navigating back to scenarios in 2 seconds...");
      timerId = setTimeout(() => {
        navigate('/scenarios');
      }, 2000); // 2-second delay
    }

    // Cleanup function to clear the timeout if the component unmounts
    // or if submitSuccess becomes false before navigation
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [submitSuccess, navigate]); // Dependencies for the effect

  const renderCheckpointItem = (item, sectionPrefix) => {
    const radioGroupName = `${sectionPrefix}-${item.id}`;

    // Determine icon based on whether it's a dynamic checkpoint or hardcoded common error
    const isCommonError = item.status !== undefined;
    const iconBgColor = isCommonError
        ? (item.status === 'passed' ? 'success.light' : 'error.light')
        : item.completed ? 'success.light' : 'error.light'; // Always error color for dynamic checkpoints (completed=false)
    const iconColor = isCommonError
        ? (item.status === 'passed' ? 'success.contrastText' : 'error.contrastText')
        : item.completed ? 'success.contrastText' : 'error.contrastText';
    const icon = isCommonError
        ? (item.status === 'passed' ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />)
        : item.completed ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />; // Always CloseIcon for dynamic checkpoints

    return (
      <Paper key={item.id} sx={{ display: 'flex', p: 2, mb: 1, alignItems: 'center', border: '1px solid #eee', borderRadius: 2 }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0,
          bgcolor: iconBgColor,
          color: iconColor,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ mb: 0.5 }}>{item.text}</Typography>
          <RadioGroup
            row
            name={radioGroupName}
            value={formState.b1_correctness[item.id] || 'correct'}
            onChange={(e) => handleRadioChange('b1_correctness', item.id, e.target.value)}
          >
            <FormControlLabel value="correct" control={<Radio size="small" />} label={tr('判斷正確', 'Judgment correct')} />
            <FormControlLabel value="incorrect" control={<Radio size="small" />} label={tr('判斷錯誤', 'Judgment incorrect')} />
          </RadioGroup>
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, pb: 12 }}>
      {/* Intro Box */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f7f9fc', borderLeft: '4px solid #3498db', boxShadow: 1 }}>
        <Typography variant="h6" fontWeight={600} color="#2980b9" mb={1.5}>{tr('驗測說明', 'Evaluation Instructions')}</Typography>
        <Typography variant="body1" color="text.secondary">
          {tr('請根據以下六項指標對系統進行驗測評估，此評估結果將直接影響系統的最終評分。預設選項已設為「正確」，若您認為系統判斷有誤，請選擇「不正確」並提供意見。', 'Evaluate the system using the six criteria below. Each item defaults to “Correct”; choose “Incorrect” and leave a comment whenever you disagree with the system.')}
        </Typography>
      </Paper>

      {/* A1 Section - Now uses fetched data */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>{tr('A1 - 節點正確性', 'A1 - Node Accuracy')}</Typography>
          <Typography variant="body2" color="text.secondary">{tr('評估 AI 對話中的節點判斷是否正確，以及對話內容是否與節點名稱匹配', 'Check whether the AI selected the correct conversation node and whether the content matches it.')}</Typography>
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>Error: {error}</Typography>
        ) : (!conversationData || !conversationData.nodes_correction || conversationData.nodes_correction.length === 0 || Object.keys(conversationData.nodes_correction[0]).length === 0) ? (
           <Typography sx={{ p: 2 }}>No conversation data available for A1 section.</Typography>
        ) : (
           Object.entries(conversationData.nodes_correction[0]).map(([nodeTitle, messages]) => (
             <Paper key={nodeTitle} sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
               <Typography variant="h6" fontWeight={600} color="#2980b9" mb={1.5}>{nodeTitle}</Typography>
               {messages.map((message, messageIndex) => {
                 const speakerText = message.role === 'user' ? tr('專員', 'Advisor') : tr('AI 客戶', 'AI Customer');
                 const a1Key = `a1-${nodeTitle}-user-${messageIndex}`;
                 const a1RadioGroupName = `a1-${nodeTitle}-user-${messageIndex}-correctness`;

                 return (
                   <Paper key={messageIndex} sx={{ display: 'flex', p: 1.5, mb: 1, alignItems: 'center', border: '1px solid #eee', borderRadius: 2 }}>
                     <Box sx={{ flex: 1 }}>
                       <Typography variant="body2" fontWeight={600} mb={0.5}>{speakerText}{tr('：', ':')}</Typography>
                       <Typography variant="body2" color="text.primary">{message.content}</Typography>
                     </Box>
                     {message.role === 'user' && (
                       <RadioGroup
                         row
                         sx={{ ml: 2 }}
                         name={a1RadioGroupName}
                         value={formState.a1_correctness[a1Key] || 'correct'}
                         onChange={(e) => handleRadioChange('a1_correctness', a1Key, e.target.value)}
                       >
                         <FormControlLabel value="correct" control={<Radio size="small" />} label={tr('正確', 'Correct')} sx={{ mr: 1 }} />
                         <FormControlLabel value="incorrect" control={<Radio size="small" />} label={tr('不正確', 'Incorrect')} />
                       </RadioGroup>
                     )}
                   </Paper>
                 );
               })}
             </Paper>
           ))
        )}
      </Paper>

       {/* A2 Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>{tr('A2 - 回應準確性', 'A2 - Response Accuracy')}</Typography>
          <Typography variant="body2" color="text.secondary">{tr('評估 AI 在陪練過程中被用戶標記為不恰當的回應', 'Review AI responses that the user marked as inappropriate during practice.')}</Typography>
        </Box>
        {(conversationData?.response_correction_rate || []).map((item, index) => {
          const a2Key = `a2-resp-${index}`;
          return (
          <Paper key={a2Key} sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
             <Box sx={{ display: 'flex', p: 1.5, mb: 1, alignItems: 'center', border: '1px solid #eee', borderRadius: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ flex: 1 }}>
                   <Typography variant="body2" fontWeight={600} mb={0.5}>{tr('AI 客戶:', 'AI Customer:')}</Typography>
                   <Typography variant="body2" color="text.primary">{item}</Typography>
                </Box>
                <StyledIconButton
                    active={formState.a2_feedback[a2Key]?.active}
                    onClick={() => handleToggleFeedback('a2_feedback', a2Key)}
                >
                   <ThumbsDownIcon />
                </StyledIconButton>
            </Box>
            {formState.a2_feedback[a2Key]?.active === false && (
                 <TextField
                   fullWidth
                   multiline
                   rows={3}
                   variant="outlined"
                   size="small"
                   placeholder={tr('請輸入您對這個 AI 回應的評論...', 'Enter your comments about this AI response...')}
                   value={formState.a2_feedback[a2Key]?.comment || ''}
                   onChange={(e) => handleTextChange('a2_feedback', a2Key, e.target.value)}
                   sx={{ mt: 1 }}
                 />
            )}
          </Paper>
        );
        })}
      </Paper>

       {/* A3 Section */}
       <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>{tr('A3 - 教學引導準確性', 'A3 - Coaching Guidance Accuracy')}</Typography>
          <Typography variant="body2" color="text.secondary">{tr('評估 AI 在陪練過程中提供的教學引導是否準確', 'Check whether the coaching guidance provided during practice was accurate.')}</Typography>
        </Box>
        {(conversationData?.advisor_correction_rate || []).map((item, index) => {
           const a3Key = `a3-guide-${index}`;
           return (
           <Paper key={a3Key} sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
               <Paper sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                   <Typography variant="body2" sx={{ mb: 1.5 }}>{item}</Typography>
                   <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                       <StyledIconButton
                         active={formState.a3_feedback[a3Key]?.active}
                         onClick={() => handleToggleFeedback('a3_feedback', a3Key)}
                       >
                           <ThumbsDownIcon />
                       </StyledIconButton>
                   </Box>
                   {formState.a3_feedback[a3Key]?.active === false && (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                        placeholder={tr('請輸入您對這個教學引導的評論...', 'Enter your comments about this guidance...')}
                        value={formState.a3_feedback[a3Key]?.comment || ''}
                        onChange={(e) => handleTextChange('a3_feedback', a3Key, e.target.value)}
                        sx={{ mt: 1 }}
                      />
                   )}
               </Paper>
           </Paper>
        );
        })}
       </Paper>


      {/* B1 Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
          {/* Inner Box for Title and Link */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            {/* Reverted Title */}
            <Typography variant="h6" fontWeight={600}>
              {tr('B1 - AI 評分準確性', 'B1 - AI Scoring Accuracy')}
            </Typography>
            {/* New Link */}
            <Link 
              to={`/history?view=${conversationId}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ textDecoration: 'underline', color: 'primary.main' }} // Using style prop directly for simplicity here
            >
              <Typography variant="body2">{tr('查看歷史訊息', 'View conversation history')}</Typography> {/* Wrap text in Typography for consistency */}
            </Link>
          </Box>
          {/* Description remains below */}
          <Typography variant="body2" color="text.secondary">{tr('評估 AI 對常見錯誤和查核點評估的準確性', 'Check the accuracy of the AI’s common-mistake and checkpoint scoring.')}</Typography>
        </Box>

        {/* Checkpoints */}
        <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} color="#2980b9" mb={1.5}>{tr('節點查核點評估', 'Node Checkpoint Evaluation')}</Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeB1Tab}
              onChange={handleB1TabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="checkpoint tabs"
            >
             {(conversationData?.nodesData?.[0]?.nodes || []).map((node) => (
                <Tab key={node.id} label={node.text} />
             ))}
            </Tabs>
          </Box>
          <Box sx={{ pt: 2 }}>
            {(() => {
              const nodes = conversationData?.nodesData?.[0]?.nodes || [];
              const currentNode = nodes[activeB1Tab];
              if (!currentNode) return <Typography>{tr('此分頁沒有資料。', 'No data for this tab.')}</Typography>;

              const originalCheckpoints = currentNode.checkpoint_status || [];
              const filteredCheckpoints = originalCheckpoints
                  .map((cp, index) => ({ ...cp, originalIndex: index }));

              if (filteredCheckpoints.length === 0) {
                return <Typography sx={{ p: 2, color: 'text.secondary' }}>{tr('此節點沒有查核點。', 'This node has no checkpoints.')}</Typography>;
              }

              return filteredCheckpoints.map(checkpoint => {
                  const itemKey = `b1-cp-${currentNode.id}-${checkpoint.originalIndex}`;
                  const item = { id: itemKey, text: checkpoint.text, completed: checkpoint.completed };
                  return renderCheckpointItem(item, 'b1_correctness');
              });
            })()}
          </Box>
        </Paper>
      </Paper>

        {/* B2 Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>{tr('B2 - 評分報告全面性', 'B2 - Report Coverage')}</Typography>
          <Typography variant="body2" color="text.secondary">{tr('評估 AI 生成的評估報告是否全面', 'Check whether the AI-generated assessment report is comprehensive.')}</Typography>
        </Box>
         <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
           <Typography variant="subtitle1" fontWeight={600} color="#2980b9" mb={1.5}>{tr('常見錯誤評估', 'Common Mistake Evaluation')}</Typography>
           <Typography variant="body2" color="text.secondary" mb={1}>{tr('請勾選系統應該檢測但未檢測出的錯誤:', 'Select errors the system should have detected but missed:')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 1 }}>
                {(conversationData?.commonMistakes || []).map(err => {
                    // Find matching AI-detected mistakes
                    const aiDetectedMistakes = conversationData?.analysisData?.common_mistakes?.filter(
                        aiMistake => aiMistake.text === err.text
                    ) || [];

                    return (
                        <Box key={err.id}>
                            <FormControlLabel
                              control={<Checkbox checked={!!formState.b2_missed[err.id]} onChange={(e) => handleCheckboxChange(err.id, e.target.checked)} size="small" />}
                              label={<Typography variant="body2">{localizeCommonMistake(err.text, isEnglish)}</Typography>}
                              sx={{ m: 0 }}
                            />
                            {/* Render original sentences if any were found */}
                            {aiDetectedMistakes.length > 0 && (
                                <Box sx={{ ml: 4, mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'warning.light' }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {tr('系統偵測到的相關語句:', 'Related statements detected by the system:')}
                                    </Typography>
                                    {aiDetectedMistakes.map((aiMistake, idx) => (
                                        <Typography key={idx} variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'warning.dark' }}>
                                            - "{aiMistake.original_sentence}"
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
         </Paper>
      </Paper>


       {/* B3 Section */}
       <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
         <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2.5 }}>
           <Typography variant="h6" fontWeight={600}>{tr('B3 - 評分報告專業度', 'B3 - Report Professionalism')}</Typography>
           <Typography variant="body2" color="text.secondary">{tr('評估 AI 給出的改善建議是否專業且有幫助', 'Check whether the AI’s recommendations are professional and useful.')}</Typography>
         </Box>
         {/* Suggestion Review */}
         {[conversationData?.analysisData?.suggestions?.overall].filter(Boolean).map((item, index) => {
           const b3Key = `b3-sug-${index}`;
           const b3RadioGroupName = `b3-sug-${b3Key}`;
           return (
             <Paper key={b3Key} sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
                 <Paper sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body1">{item}</Typography>
                    <RadioGroup
                      row sx={{ mt: 1.5 }}
                      name={b3RadioGroupName}
                      value={formState.b3_expectation[b3Key] || 'correct'}
                      onChange={(e) => handleRadioChange('b3_expectation', b3Key, e.target.value)}
                    >
                        <FormControlLabel value="correct" control={<Radio size="small"/>} label={tr('符合預期', 'Meets expectations')} sx={{mr: 1}} />
                        <FormControlLabel value="incorrect" control={<Radio size="small"/>} label={tr('未符合預期', 'Does not meet expectations')} />
                    </RadioGroup>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder={tr('請輸入您對這個改善建議的評論...', 'Enter your comments about this recommendation...')}
                      value={formState.b3_comment[b3Key] || ''}
                      onChange={(e) => handleTextChange('b3_comment', b3Key, e.target.value)}
                      sx={{ mt: 1 }}
                    />
                 </Paper>
             </Paper>
           );
         })}

         {/* "2個提問1個舉例" Analysis Display & Evaluation */}
         {conversationData?.analysisData?.two_questions_one_example_analysis && (
           <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
             <Typography variant="subtitle1" fontWeight={600} color="#2980b9" mb={1.5}>
               {tr('「2個提問1個舉例」模式分析', '“Two Questions, One Example” Analysis')}
             </Typography>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
               <Typography variant="body2" fontWeight="bold">{tr('觀察狀態:', 'Observed:')}</Typography>
               {conversationData.analysisData.two_questions_one_example_analysis.observed ? (
                 <Chip
                   icon={<CheckCircleIcon fontSize="small"/>}
                   label={tr('有觀察到', 'Observed')}
                   color="success"
                   size="small"
                   variant="outlined"
                 />
               ) : (
                 <Chip
                   icon={<CancelIcon fontSize="small"/>}
                   label={tr('未觀察到', 'Not observed')}
                   color="warning"
                   size="small"
                   variant="outlined"
                 />
               )}
             </Box>
             <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
               {tr('AI 建議:', 'AI suggestion:')}
             </Typography>
             <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
               {conversationData.analysisData.two_questions_one_example_analysis.suggestion || tr('無建議提供。', 'No suggestion provided.')}
             </Typography>
             {/* Display fulfilling sentences if observed is true and sentences exist */}
             {conversationData.analysisData.two_questions_one_example_analysis.observed &&
              conversationData.analysisData.two_questions_one_example_analysis.fulfilling_sentences &&
              conversationData.analysisData.two_questions_one_example_analysis.fulfilling_sentences.length > 0 && (
               <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                 <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                   {tr('達成模式的語句:', 'Matching statements:')}
                 </Typography>
                 {conversationData.analysisData.two_questions_one_example_analysis.fulfilling_sentences.map((sentence, idx) => (
                   <Typography key={idx} variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'info.dark', mb: 0.5 }}>
                     - "{sentence}"
                   </Typography>
                 ))}
               </Box>
             )}
             <RadioGroup
               row
               name="b3-tqoe-analysis-0-evaluation"
               value={formState.b3_tqoe_expectation?.['b3-tqoe-analysis-0'] || 'correct'}
               onChange={(e) => handleRadioChange('b3_tqoe_expectation', 'b3-tqoe-analysis-0', e.target.value)}
             >
               <FormControlLabel value="correct" control={<Radio size="small"/>} label={tr('符合預期', 'Meets expectations')} sx={{mr: 1}} />
               <FormControlLabel value="incorrect" control={<Radio size="small"/>} label={tr('未符合預期', 'Does not meet expectations')} />
             </RadioGroup>
           </Paper>
         )}

         {/* Overall Rating */}
         <Paper sx={{ p: 2.5, bgcolor: '#f9fafc', borderRadius: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} color="#2980b9" mb={1.5} textAlign="center">{tr('整體專業度評分', 'Overall Professionalism Rating')}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                 <Rating
                   name="b3-rating"
                   value={formState.b3_rating}
                   onChange={handleRatingChange}
                 />
            </Box>
         </Paper>
       </Paper>


      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || submitSuccess}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
            {isSubmitting ? tr('提交中...', 'Submitting...') : (submitSuccess ? tr('已提交', 'Submitted') : tr('提交評估', 'Submit Evaluation'))}
        </Button>
        <Button variant="outlined" color="secondary" onClick={() => window.history.back()} disabled={isSubmitting}>
            {tr('重新練習', 'Practice Again')}
        </Button>
      </Box>

      {/* Submission Feedback Snackbar */}
      <Snackbar
        open={Boolean(submitError) || submitSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={submitError ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {submitError ? submitError : tr('評估已成功提交！', 'Evaluation submitted successfully!')}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default ChatEvaluation;
